import React, { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  ChartBarIcon,
  CalendarIcon,
  UserGroupIcon,
  BanknotesIcon,
  DocumentChartBarIcon,
  ArrowDownTrayIcon,
  EyeIcon
} from '@heroicons/react/24/outline'
import api from '@/services/api'
import toast from 'react-hot-toast'
// import { useAuth } from '@/hooks/useAuth'

interface ReportData {
  totalEmployees: number
  activeEmployees: number
  inactiveEmployees: number
  totalPayrollRuns: number
  currentMonthPayroll: number
  totalLoansActive: number
  totalLeaveRequests: number
  pendingLeaveRequests: number
}

const Reports: React.FC = () => {
  // const { user } = useAuth()
  const [selectedReport, setSelectedReport] = useState<string | null>(null)
  const [dateRange, setDateRange] = useState({
    startDate: new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0],
    endDate: new Date().toISOString().split('T')[0]
  })

  // Fetch dashboard data
  const { data: reportData, isLoading } = useQuery({
    queryKey: ['reports-dashboard'],
    queryFn: async (): Promise<ReportData> => {
      const response = await api.get('/reports/dashboard')
      return response.data.data
    }
  })

  const reportTypes = [
    {
      id: 'employee-summary',
      title: 'Employee Summary Report',
      description: 'Overview of all employees by status, department, and hire date',
      icon: UserGroupIcon,
      color: 'bg-indigo-600',
      endpoint: '/reports/employees'
    },
    {
      id: 'payroll-summary',
      title: 'Payroll Summary Report',
      description: 'Monthly payroll totals, taxes, and deductions',
      icon: BanknotesIcon,
      color: 'bg-emerald-600',
      endpoint: '/reports/payroll'
    },
    {
      id: 'leave-analysis',
      title: 'Leave Analysis Report',
      description: 'Leave requests, balances, and patterns by department',
      icon: CalendarIcon,
      color: 'bg-violet-600',
      endpoint: '/reports/leaves'
    },
    {
      id: 'loan-status',
      title: 'Loan Status Report',
      description: 'Active loans, payments, and outstanding balances',
      icon: DocumentChartBarIcon,
      color: 'bg-amber-600',
      endpoint: '/reports/loans'
    },
    {
      id: 'audit-log',
      title: 'System Audit Report',
      description: 'User activities, logins, and system changes',
      icon: ChartBarIcon,
      color: 'bg-rose-600',
      endpoint: '/reports/audit'
    }
  ]

  const handleDownloadReport = async (reportId: string, endpoint: string) => {
    try {
      const response = await api.get(`${endpoint}/export`, {
        params: {
          startDate: dateRange.startDate,
          endDate: dateRange.endDate
        },
        responseType: 'blob'
      })

      const blob = new Blob([response.data], { type: 'text/csv' })
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = `${reportId}-${dateRange.startDate}-to-${dateRange.endDate}.csv`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)

      toast.success('Report downloaded successfully')
    } catch (error) {
      console.error('Download error:', error)
      toast.error('Failed to download report')
    }
  }

  const handleViewReport = async (reportId: string, endpoint: string) => {
    try {
      setSelectedReport(reportId)

      const response = await api.get(endpoint, {
        params: {
          startDate: dateRange.startDate,
          endDate: dateRange.endDate
        }
      })

      // Create a simple report view modal or redirect
      const reportData = response.data.data
      let message = `📊 ${reportId.toUpperCase()} REPORT\n\n`

      if (reportData.employees) {
        message += `Total Employees: ${reportData.totalCount}\n`
        message += `Departments: ${reportData.departmentStats?.length || 0}\n`
        message += `Sample Records: ${Math.min(5, reportData.employees.length)}\n\n`
        if (reportData.employees.length > 0) {
          message += 'Recent Employees:\n'
          reportData.employees.slice(0, 3).forEach((emp: any) => {
            message += `• ${emp.firstName} ${emp.lastName} (${emp.employeeNumber}) - ${emp.department}\n`
          })
        }
      } else if (reportData.payrollRuns) {
        message += `Total Payroll Runs: ${reportData.totalCount}\n`
        message += `Sample Records: ${Math.min(5, reportData.payrollRuns.length)}\n\n`
        if (reportData.payrollRuns.length > 0) {
          message += 'Recent Payroll Runs:\n'
          reportData.payrollRuns.slice(0, 3).forEach((run: any) => {
            message += `• ${run.payrollMonth}/${run.payrollYear} - ${run.status} (${run.totalEmployees} employees)\n`
          })
        }
      } else if (reportData.leaveRequests) {
        message += `Total Leave Requests: ${reportData.totalCount}\n`
        message += `Sample Records: ${Math.min(5, reportData.leaveRequests.length)}\n\n`
        if (reportData.leaveRequests.length > 0) {
          message += 'Recent Leave Requests:\n'
          reportData.leaveRequests.slice(0, 3).forEach((req: any) => {
            message += `• ${req.employee.firstName} ${req.employee.lastName} - ${req.leaveType.name} (${req.status})\n`
          })
        }
      } else if (reportData.loanApplications) {
        message += `Total Loan Applications: ${reportData.totalCount}\n`
        message += `Sample Records: ${Math.min(5, reportData.loanApplications.length)}\n\n`
        if (reportData.loanApplications.length > 0) {
          message += 'Recent Loan Applications:\n'
          reportData.loanApplications.slice(0, 3).forEach((loan: any) => {
            message += `• ${loan.employee.firstName} ${loan.employee.lastName} - ${loan.loanType.name} ($${loan.requestedAmount}) - ${loan.status}\n`
          })
        }
      } else if (reportData.auditLogs) {
        message += `Total Audit Logs: ${reportData.totalCount}\n`
        message += `Sample Records: ${Math.min(5, reportData.auditLogs.length)}\n\n`
        if (reportData.auditLogs.length > 0) {
          message += 'Recent Admin Actions:\n'
          reportData.auditLogs.slice(0, 3).forEach((log: any) => {
            const adminName = `${log.adminUser.firstName} ${log.adminUser.lastName}`
            const targetName = log.targetUser ? `${log.targetUser.firstName} ${log.targetUser.lastName}` : 'N/A'
            message += `• ${log.action} by ${adminName} → ${targetName} (${new Date(log.createdAt).toLocaleDateString()})\n`
          })
        }
      }

      message += `\n📅 Date Range: ${dateRange.startDate} to ${dateRange.endDate}`
      message += `\n⏰ Generated: ${new Date().toLocaleString()}`
      message += '\n\n💡 Use the Download button to get the complete CSV file.'

      alert(message)

    } catch (error) {
      console.error('View error:', error)
      toast.error('Failed to load report data')
    } finally {
      setSelectedReport(null)
    }
  }

  if (isLoading) {
    return (
      <div className="max-w-7xl mx-auto space-y-5">
        <div className="flex items-center justify-center h-64">
          <div className="h-8 w-8 rounded-full border-[3px] border-indigo-600 border-t-transparent animate-spin" />
        </div>
      </div>
    )
  }

  return (
    <div className="max-w-7xl mx-auto space-y-5">
      {/* Page Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
        <div className="flex items-center gap-4">
          <div className="h-10 w-10 flex items-center justify-center rounded-lg bg-indigo-600 text-white">
            <ChartBarIcon className="h-5 w-5" />
          </div>
          <div>
            <h1 className="text-xl font-semibold text-gray-900 dark:text-white">System Reports</h1>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Generate and download comprehensive reports for system analysis
            </p>
          </div>
        </div>
        <div className="mt-4 lg:mt-0 text-sm text-gray-500 dark:text-gray-400">
          {new Date().toLocaleDateString("en-US", {
            weekday: "long",
            month: "long",
            day: "numeric",
            year: "numeric",
          })}
        </div>
      </div>

      {/* Date Range & Header */}
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 shadow-card flex flex-col md:flex-row gap-4 items-center">
        <div className="flex-1">
          <h2 className="text-base font-semibold text-gray-900 dark:text-white">
            Report Generation Center
          </h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Generate comprehensive reports for all system activities
          </p>
        </div>

        {/* Date Range Selector */}
        <div className="flex items-center space-x-4">
          <div>
            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300">From Date</label>
            <input
              type="date"
              value={dateRange.startDate}
              onChange={(e) => setDateRange(prev => ({ ...prev, startDate: e.target.value }))}
              className="mt-1 block w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-600 focus:border-indigo-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300">To Date</label>
            <input
              type="date"
              value={dateRange.endDate}
              onChange={(e) => setDateRange(prev => ({ ...prev, endDate: e.target.value }))}
              className="mt-1 block w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-600 focus:border-indigo-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
            />
          </div>
        </div>
      </div>

      {/* Dashboard Summary */}
      {reportData && (
        <div>
          <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-4">System Overview</h3>
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 shadow-card">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <UserGroupIcon className="h-8 w-8 text-indigo-600" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 dark:text-gray-400 truncate">Total Employees</dt>
                    <dd className="text-2xl font-bold text-gray-900 dark:text-white">{reportData.totalEmployees}</dd>
                  </dl>
                </div>
              </div>
            </div>

            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 shadow-card">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <BanknotesIcon className="h-8 w-8 text-emerald-600" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 dark:text-gray-400 truncate">Payroll Runs</dt>
                    <dd className="text-2xl font-bold text-gray-900 dark:text-white">{reportData.totalPayrollRuns}</dd>
                  </dl>
                </div>
              </div>
            </div>

            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 shadow-card">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <CalendarIcon className="h-8 w-8 text-violet-600" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 dark:text-gray-400 truncate">Leave Requests</dt>
                    <dd className="text-2xl font-bold text-gray-900 dark:text-white">{reportData.totalLeaveRequests}</dd>
                  </dl>
                </div>
              </div>
            </div>

            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 shadow-card">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <DocumentChartBarIcon className="h-8 w-8 text-amber-600" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 dark:text-gray-400 truncate">Active Loans</dt>
                    <dd className="text-2xl font-bold text-gray-900 dark:text-white">{reportData.totalLoansActive}</dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Available Reports */}
      <div>
        <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-4">Available Reports</h3>
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {reportTypes.map((report) => {
            const IconComponent = report.icon
            return (
              <div
                key={report.id}
                className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 shadow-card hover:shadow-md transition-shadow duration-200"
              >
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <div className={`h-12 w-12 rounded-lg ${report.color} flex items-center justify-center`}>
                      <IconComponent className="h-6 w-6 text-white" />
                    </div>
                  </div>
                  <div className="ml-4">
                    <h4 className="text-sm font-semibold text-gray-900 dark:text-white">{report.title}</h4>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">{report.description}</p>
                  </div>
                </div>

                <div className="mt-5 flex space-x-3">
                  <button
                    onClick={() => handleViewReport(report.id, report.endpoint)}
                    className="flex-1 inline-flex justify-center items-center px-4 py-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 rounded-lg text-sm font-medium hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-600"
                  >
                    <EyeIcon className="h-4 w-4 mr-2" />
                    View
                  </button>
                  <button
                    onClick={() => handleDownloadReport(report.id, report.endpoint)}
                    className="flex-1 inline-flex justify-center items-center px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-sm font-medium"
                  >
                    <ArrowDownTrayIcon className="h-4 w-4 mr-2" />
                    Download
                  </button>
                </div>
              </div>
            )
          })}
        </div>
      </div>

      {/* Report Generation Note */}
      <div className="bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-200 dark:border-indigo-800 rounded-xl p-5">
        <div className="flex">
          <div className="flex-shrink-0">
            <ChartBarIcon className="h-5 w-5 text-indigo-600 dark:text-indigo-400" />
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-indigo-800 dark:text-indigo-300">Report Generation</h3>
            <div className="mt-2 text-sm text-indigo-700 dark:text-indigo-400">
              <p>
                Reports are generated based on the selected date range. PDF exports include detailed data and visualizations. Large reports may take a few moments to generate. For custom reports, contact the system administrator.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Loading Modal */}
      {selectedReport && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6 max-w-lg w-full">
            <h2 className="text-lg font-bold text-gray-900 dark:text-white">Report: {selectedReport}</h2>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">Fetching preview...</p>
            <button
              onClick={() => setSelectedReport(null)}
              className="mt-4 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-sm font-medium"
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

export default Reports